import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import InnerImageZoom, { InnerImageZoomProps } from 'react-inner-image-zoom'
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css'
import { useTranslation } from 'react-i18next'

import NoProviewSvg from 'assets/no-preview.svg'

import Box from 'components/Box'
import Button from 'components/Buttons/Button'
import Icon from 'components/Icon'
import IconButton from 'components/Buttons/IconButton'
import Spinner from 'components/Spinner'
import Text from 'components/Text'

import { Document } from 'types/global'
import { isImage } from 'utils/file'
import { formatDatetime } from 'utils/datetime'
import { Z_INDEX } from 'utils/constants'

type DocumentPreview = Document & Pick<InnerImageZoomProps, 'zoomScale' | 'zoomType'>

type Props = DocumentPreview & {
  onClose: () => void
  onDelete?: (documentId: string) => void
  onDownload?: (documentId: string) => void
  isReadOnly?: boolean
}

const NoPreview: FC = () => (
  <Box alignItems="center" display="flex" flexDirection="column" justifyContent="center">
    <NoProviewSvg />
    <Text color="white" fontSize={14} lineHeight="22px" mt={26}>
      document_preview_unavailable
    </Text>
  </Box>
)

const ImageContent: FC<DocumentPreview> = ({ documentUrl, zoomScale = 2, zoomType = 'hover', ...props }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [showFallback, setShowFallback] = useState(false)

  return (
    <div>
      {isLoading && (
        <>
          <Spinner fontSize={16} />
          <ImageChecker
            onError={() => {
              setIsLoading(false)
              setShowFallback(true)
            }}
            onLoad={() => setIsLoading(false)}
            src={documentUrl}
          />
        </>
      )}
      {showFallback ? (
        <NoPreview />
      ) : (
        !isLoading && <InnerImageZoom {...props} hideHint src={documentUrl} zoomScale={zoomScale} zoomType={zoomType} />
      )}
    </div>
  )
}

const DocumentView: FC<Props> = ({ documentId, onClose, onDelete, onDownload, isReadOnly, ...props }) => {
  const { fileName, mimeType, uploadedTs, uploader } = props
  const { t } = useTranslation()

  const { isDocuImage } = useMemo(
    () => ({
      isDocuImage: isImage(mimeType),
    }),
    [mimeType],
  )

  return (
    <Overlay>
      <Container>
        <Header>
          <Box alignItems="center" display="flex">
            <Icon fontSize={32} icon={isDocuImage ? ['fal', 'file-image'] : ['fal', 'file-alt']} />
            <Box ml={20}>
              <Text color="white" fontSize={13} lineHeight="22px" mb="2px">
                {fileName}
              </Text>
              <Text color="gray5" fontSize={13} lineHeight="16px">
                {t('document_upload_info', { name: uploader, time: formatDatetime(uploadedTs) })}
              </Text>
            </Box>
          </Box>
          <Box display="flex">
            {onDownload && (
              <DownloadButton
                icon="arrow-to-bottom"
                iconColor="white"
                mr={26}
                onClick={() => onDownload(documentId)}
                variant="ghost"
              >
                download_file
              </DownloadButton>
            )}
            {onDelete && !isReadOnly && (
              <Button icon={['far', 'trash-alt']} mr={24} onClick={() => onDelete(documentId)} variant="criticalGhost">
                delete_lbl
              </Button>
            )}
            <CloseButton icon={['far', 'times']} onClick={onClose} />
          </Box>
        </Header>
        <Content>{isDocuImage ? <ImageContent documentId={documentId} {...props} /> : <NoPreview />}</Content>
      </Container>
    </Overlay>
  )
}

const CloseButton = styled(IconButton)`
  & svg {
    color: ${(p) => p.theme.colors.gray5};
  }
`

const Container = styled.div`
  background-color: ${(p) => p.theme.colors.gray7};
  display: flex;
  flex-direction: column;
  min-width: 1440px;
  width: 100%;
`

const Content = styled.div`
  align-items: center;
  display: flex;
  flex: 1 0 auto;
  justify-content: center;
  width: 100%;
`

const DownloadButton = styled(Button)`
  color: ${(p) => p.theme.colors.white};

  & svg {
    color: ${(p) => p.theme.colors.white};
  }
`

const Header = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.gray5};
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  padding: 16px 36px;
  user-select: none;
`

const ImageChecker = styled.img`
  display: none;
`

const Overlay = styled.div`
  background-color: ${(p) => p.theme.colors.black};
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${Z_INDEX.window};
`

export default DocumentView
